/**
 * Sending product events to Google Tag Manager.
 *
 * `track("exam_start", {...})` is the whole interface; what may be sent is
 * `src/lib/analytics-events.ts`, and TypeScript refuses anything not in it.
 * What the container does with an event is Tag Manager's business — one GA4
 * tag takes every name in the catalogue, the Meta Pixel takes the handful in
 * `META_EVENTS` — and `scripts/gtm/container.ts` builds that configuration from
 * the same catalogue.
 *
 * Every push is two: one that clears `event_params` and `ecommerce`, then the
 * event. Tag Manager merges pushes into one running model, so without the
 * clear a `module` from the last event would ride along on the next one that
 * has none — the same reason Google's own ecommerce examples push
 * `ecommerce: null` first.
 *
 * Nothing is sent before consent. `isTracking()` is false until the container
 * has been asked for, and a push made before then is dropped, not queued: the
 * container would replay a queued event after a later "accept" as though it
 * had been consented to at the time.
 */
import { useEffect } from "react";
import { ROUTES } from "@/routes";
import { match, splitLocale, useFullPath } from "@/lib/router";
import { isTracking, onTrackingStart } from "@/state/cookie-consent";
import { variantById } from "@shared/variants";
import type { SetSummary } from "@shared/public";
import { isPack, type Product } from "@shared/plans";
import { chargeMinor } from "@shared/promo";
import type { ContextParams, EventName, Events, SetParams } from "./analytics-events";

type Ecommerce = {
  currency: string;
  value: number;
  transaction_id?: string;
  items: { item_id: string; item_name: string; price: number; quantity: number }[];
};

let signedIn = false;

/** The session provider says who is here; every event carries it. */
export function setSignedIn(value: boolean): void {
  signedIn = value;
}

/** The route is the pattern, not the path: `/result/:attemptId`, once, not once per result. */
function context(): ContextParams {
  const { locale, path } = splitLocale(window.location.pathname);
  const def = ROUTES.find((r) => match(r.pattern, path) !== null);
  return {
    site_language: locale,
    page_type: def && "public" in def && def.public ? "marketing" : "app",
    route: def?.pattern ?? "(not found)",
    signed_in: signedIn,
  };
}

function push(event: EventName, params: object, ecommerce?: Ecommerce): void {
  if (!isTracking()) return;
  const layer = (window.dataLayer ??= []);
  layer.push({ event_params: null, ecommerce: null });
  layer.push({
    event,
    event_params: { ...context(), ...params },
    ...(ecommerce ? { ecommerce } : {}),
  });
}

type ArgsOf<E extends EventName> = keyof Events[E] extends never ? [] : [params: Events[E]];

export function track<E extends EventName>(event: E, ...[params]: ArgsOf<E>): void {
  push(event, params ?? {});
}

/** The ecommerce events, whose items travel beside the parameters. */
export function trackCommerce<E extends "view_item" | "select_item" | "begin_checkout" | "checkout_cancel" | "purchase">(
  event: E,
  plan: Product,
  params: Events[E],
  transactionId?: string,
): void {
  const price = chargeMinor(plan) / 100;
  push(event, params, {
    currency: plan.currency.toUpperCase(),
    value: price,
    ...(transactionId ? { transaction_id: transactionId } : {}),
    items: [
      {
        item_id: plan.id,
        item_name: isPack(plan) ? `Oral ${plan.sessions}` : `Pass ${plan.days}`,
        price,
        quantity: 1,
      },
    ],
  });
}

/** What a report needs to know about a set, and nothing that identifies it. */
export function setParams(set: SetSummary | null | undefined): SetParams | null {
  if (!set) return null;
  const variant = variantById(set.variant);
  return {
    exam_family: variant.family,
    exam_variant: variant.id,
    level: variant.levels.join("-"),
    set_source: set.source,
    set_free: set.free,
  };
}

/**
 * One page view per URL, the first included. Mounted once, in the shell.
 *
 * An effect, so it runs after the page's own `PageMeta` has set the title the
 * view is reported under. It listens to the path, so the back button counts,
 * and a language switch — a different URL — counts as a page of its own.
 */
export function usePageViews(): void {
  const path = useFullPath();
  useEffect(() => {
    track("page_view");
  }, [path]);
}

// Consent given in the banner: the page on screen was never reported.
onTrackingStart(() => track("page_view"));

/**
 * `/attempts/abc/answers` → `/attempts/:id/answers`, for `api_error`. The ids
 * are UUIDs, or a set's derived one; either way they say nothing a report can
 * group by.
 */
export function endpointOf(path: string): string {
  return path
    .split("?")[0]!
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id");
}
